import FilterMultiselect from "@/components/shared/FilterMultiselect";
import { useDocumentTypes } from "@/hooks/useDocumentTypes";
import { useEffect, useState } from "react";

interface DocumentTypeFilterProps {
  /** Currently selected document types */
  value: string[];
  /** Callback when selection changes */
  onChange: (types: string[]) => void;
  /** Callback when filter is removed */
  onRemove: () => void;
  /** Whether to open immediately */
  initialOpen?: boolean;
}

/**
 * DocumentTypeFilter component - Multi-select dropdown for document types
 *
 * Uses the useDocumentTypes hook to fetch available types from the backend.
 * Allows selecting multiple document types for filtering.
 */
export function DocumentTypeFilter({
  value,
  onChange,
  onRemove,
  initialOpen = false,
}: DocumentTypeFilterProps) {
  const { data: documentTypes = [], isLoading } = useDocumentTypes();

  // Track if we should open when loading completes
  // This preserves initialOpen through the loading state
  const [shouldOpenWhenLoaded, setShouldOpenWhenLoaded] = useState(initialOpen);

  // Update shouldOpenWhenLoaded when initialOpen changes
  useEffect(() => {
    if (initialOpen) {
      setShouldOpenWhenLoaded(true);
    }
  }, [initialOpen]);

  // Clear the flag after we've used it (after loading completes)
  useEffect(() => {
    if (!isLoading && shouldOpenWhenLoaded) {
      // Small delay to let FilterMultiselect mount first
      const timer = setTimeout(() => setShouldOpenWhenLoaded(false), 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading, shouldOpenWhenLoaded]);

  // Show loading state in the filter
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-[#232f48] border border-gray-200 dark:border-[#324467] rounded-full text-xs">
        <span className="font-semibold text-gray-600 dark:text-gray-300">Type:</span>
        <span className="text-gray-400 dark:text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <FilterMultiselect
      id="documentType"
      label="Type"
      options={documentTypes}
      value={value}
      initialOpen={shouldOpenWhenLoaded || initialOpen}
      getDisplayValue={(type) => type}
      getSelectedDisplayValue={(selected) => {
        if (selected.length === 0) return "All";
        if (selected.length === 1) return selected[0];
        return `${selected.length} types`;
      }}
      onChange={(newValue) => onChange([...newValue])}
      onRemove={() => onRemove()}
      direction="column"
    />
  );
}
